import { useEffect, useState } from "react";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/api";
import { inputClass } from "@/components/FieldInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ACTIVITY_TYPES } from "@/components/activities/types";

export function TypeBadge({ type }) {
  const t = ACTIVITY_TYPES[type] || { label: type, cls: "bg-slate-100 text-slate-700 border-slate-200" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${t.cls}`} data-testid={`activity-type-${type}`}>{t.label}</span>;
}

function Field({ label, children, className = "" }) {
  return <div className={className}><dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</dt><dd className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{children}</dd></div>;
}

export function ActivityDetailDialog({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl bg-white" data-testid="activity-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading">{item?.title}</DialogTitle></DialogHeader>
        {item && (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-5 py-2">
            <Field label="Data">{fmtDate(item.date)}</Field>
            <Field label="Tipologia"><TypeBadge type={item.type} /></Field>
            <Field label="Descrizione" className="sm:col-span-2">{item.description || "—"}</Field>
            <Field label="Durata">{Number(item.hours).toLocaleString("it-IT")} ore</Field>
            <Field label="Costo informativo">{item.cost != null ? fmtMoney(item.cost) : "—"}</Field>
            <Field label="Inserita da">{item.author_name} · {fmtDateTime(item.created_at)}</Field>
            <Field label="Ultima modifica">{item.updated_at ? `${item.updated_by_name} · ${fmtDateTime(item.updated_at)}` : "—"}</Field>
          </dl>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="activity-detail-close">Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { date: "", type: "online", title: "", description: "", hours: "", cost: "" };

export function ActivityFormDialog({ item, onClose, onSave, saving }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (item) setF({ ...EMPTY, ...item, description: item.description || "", hours: item.hours ?? "", cost: item.cost ?? "" }); }, [item]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const lab = (l, req) => <label className="block text-xs font-semibold text-slate-600 mb-1.5">{l}{req && <span className="text-red-500"> *</span>}</label>;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl bg-white" data-testid="activity-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{item?.id ? "Modifica attività" : "Nuova attività"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>{lab("Data", true)}<input type="date" className={inputClass} value={f.date} onChange={set("date")} data-testid="activity-field-date" /></div>
          <div>{lab("Tipologia", true)}<select className={inputClass} value={f.type} onChange={set("type")} data-testid="activity-field-type">{Object.entries(ACTIVITY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
          <div className="sm:col-span-2">{lab("Titolo", true)}<input className={inputClass} value={f.title} onChange={set("title")} placeholder="Es. Incontro con il Disponente" data-testid="activity-field-title" /></div>
          <div className="sm:col-span-2">{lab("Descrizione")}<textarea rows={4} className={inputClass} value={f.description} onChange={set("description")} data-testid="activity-field-description" /></div>
          <div>{lab("Durata (ore)", true)}<input type="number" step="0.25" min="0" className={inputClass} value={f.hours} onChange={set("hours")} placeholder="1,5" data-testid="activity-field-hours" /></div>
          <div>{lab("Costo informativo (€)")}<input type="number" step="0.01" min="0" className={inputClass} value={f.cost} onChange={set("cost")} placeholder="facoltativo" data-testid="activity-field-cost" /></div>
        </div>
        <p className="text-xs text-slate-500">I costi sono informativi e non generano movimenti contabili.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="activity-form-cancel">Annulla</Button>
          <Button onClick={() => onSave(f)} disabled={saving} data-testid="activity-form-save" className="bg-[#0A2540] hover:bg-[#031B33] text-white">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
